import { useEffect, useRef } from 'react';

const insights = [
  {
    icon: 'ri-cloud-line',
    title: 'Seasonal Patterns',
    desc: 'Analysis of data (2000–2024) shows a consistent rise in pollution levels during monsoon months, driven by increased agricultural runoff and urban discharge entering the river system.',
    color: 'bg-sky-50 border-sky-200',
    iconBg: 'bg-sky-100 text-sky-600',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Industrial Impact',
    desc: 'Industrial discharge significantly contributes to organic load and chemical contamination, with several high-density industrial zones consistently exceeding safe limits.',
    color: 'bg-emerald-50 border-emerald-200',
    iconBg: 'bg-emerald-100 text-emerald-600',
  },
  {
    icon: 'ri-building-line',
    title: 'Urban Pollution',
    desc: 'Urban sewage and untreated wastewater remain major pollution sources, with multiple cities showing sustained high pollution levels due to inadequate treatment infrastructure.',
    color: 'bg-teal-50 border-teal-200',
    iconBg: 'bg-teal-100 text-teal-600',
  },
  {
    icon: 'ri-plant-line',
    title: 'Agricultural Runoff',
    desc: 'Runoff from agricultural activities introduces nutrients and chemicals into the river, contributing to eutrophication and increased biological contamination levels.',
    color: 'bg-green-50 border-green-200',
    iconBg: 'bg-green-100 text-green-600',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Critical Hotspots',
    desc: 'Data analysis identifies key pollution hotspots along the Ganga where BOD and COD levels frequently exceed permissible limits, requiring targeted intervention and monitoring.',
    color: 'bg-orange-50 border-orange-200',
    iconBg: 'bg-orange-100 text-orange-600',
  },
  {
    icon: 'ri-tools-line',
    title: 'Treatment Infrastructure Gap',
    desc: 'Data analysis (2000–2024) reveals a significant gap between sewage generation and treatment capacity, resulting in large volumes of untreated wastewater entering the river system.',
    color: 'bg-slate-50 border-slate-200',
    iconBg: 'bg-slate-100 text-slate-600',
  },
];

export default function DataReveals() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.card-animate').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
                (el as HTMLElement).classList.remove('opacity-0', 'translate-y-8');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="data" className="py-24 md:py-32 bg-gradient-to-b from-[#EDE0D4]/60 to-[#F8F5F0]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16 card-animate opacity-0 translate-y-8 transition-all duration-700">
          <p className="text-[#2A9D8F] text-sm uppercase tracking-[0.25em] font-semibold mb-3">Research Findings</p>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            What the Data Reveals
          </h2>
          <p className="text-[#6C757D] text-base max-w-xl mx-auto">
            Years of data analysis uncovered clear patterns in how and why the Ganga is being polluted.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {insights.map((item) => (
            <div
              key={item.title}
              className={`card-animate opacity-0 translate-y-8 transition-all duration-700 rounded-2xl border p-6 ${item.color} hover:-translate-y-1 cursor-default`}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${item.iconBg}`}>
                <i className={`${item.icon} text-xl`} />
              </div>
              <h3 className="text-[#1C1C1C] font-semibold text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                {item.title}
              </h3>
              <p className="text-[#6C757D] text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Data Sources & Validation */}
        <div className="card-animate opacity-0 translate-y-8 transition-all duration-700 mt-16 rounded-2xl border border-[#EDE0D4] bg-[#F8F5F0] px-8 md:px-12 py-10">
          <div className="flex flex-col md:flex-row md:items-start gap-8">
            <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl bg-[#EDE0D4] text-[#2A9D8F]">
              <i className="ri-shield-check-line text-xl" />
            </div>
            <div className="flex-1">
              <h3
                className="text-[#1C1C1C] font-bold text-xl md:text-2xl mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Data Sources &amp; Validation
              </h3>
              <p className="text-[#6C757D] text-sm mb-5">
                This analysis is based on verified government datasets collected from:
              </p>
              <ul className="flex flex-col sm:flex-row gap-3 sm:gap-6 mb-6">
                {[
                  { icon: 'ri-government-line', label: 'Namami Gange Programme' },
                  { icon: 'ri-bar-chart-2-line', label: 'Central Pollution Control Board (CPCB)' },
                  { icon: 'ri-drop-line', label: 'Ministry of Jal Shakti' },
                ].map((src) => (
                  <li key={src.label} className="flex items-center gap-2 text-[#1C1C1C] text-sm font-medium">
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#EDE0D4] text-[#2A9D8F] flex-shrink-0">
                      <i className={`${src.icon} text-base`} />
                    </span>
                    {src.label}
                  </li>
                ))}
              </ul>
              <p className="text-[#6C757D] text-sm leading-relaxed border-t border-[#EDE0D4] pt-5">
                Based on government-verified datasets <strong className="text-[#1C1C1C]">(2000–2024)</strong>, ensuring accuracy, reliability, and real-world environmental insights.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
